import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import {
  Chip,
  ChipBackgroundColor,
  ChipSize,
} from "@amzn/stencil-react-components/chip";
import { FormWrapper } from "@amzn/stencil-react-components/form";
import {
  InputFooter,
  InputWrapper,
} from "@amzn/stencil-react-components/input";
import { Col, Row, Spacer, View } from "@amzn/stencil-react-components/layout";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "@amzn/stencil-react-components/modal";
import { DetailedRadio, Radio } from "@amzn/stencil-react-components/radio";
import { Label, Text } from "@amzn/stencil-react-components/text";
import { TextArea } from "@amzn/stencil-react-components/text-area";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  publishSecurityKeyIssuesFormCanceledMetrics,
  publishSecurityKeyIssuesFormSubmittedMetrics,
  publishSecurityKeyNotArrivedMetric,
  publishSecurityKeyNotWithMeMetric,
  publishSecurityKeyOtherIssueMetric,
} from "@/apis/Metrics";

const TEXT_AREA_HEIGHT = 150;
const MAX_DETAILS_LENGTH = 1000;

/**
 * Configuration interface for radio button options in the form.
 * Defines the structure for selectable options in question sections.
 */
export interface RadioOption {
  /** Unique identifier for the radio button element */
  id: string;
  /** Value submitted when this option is selected */
  value: string;
  /** ID of the display text shown to the user for this option */
  labelMessageId: string;
}

/**
 * Configuration object for customizing the form based on different scenarios.
 * Allows the form to be adapted for different error contexts (general vs timeout).
 */
export interface FormConfig {
  /** ID of the text to display in the warning banner at the top of the form */
  warningBannerMessageId: string;
  /** Radio button options for question 1, excluding "Other" which is always added automatically */
  question1Options: RadioOption[];
}

/**
 * Form data interface containing all user-provided information about their security key issue.
 * This data is collected and submitted for processing and metrics collection.
 */
export interface ReportFormData {
  /** The reason why the user is reporting an issue (e.g., "not_arrived", "not_with_me", "other") */
  selectedReason: string;
  /** The preferred delivery method for replacement key (e.g., "it_vending_machine", "chat_with_it") */
  selectedDelivery: string;
  /** Additional details when "other" reason is selected, undefined if not applicable */
  otherReasonDetails: string | undefined;
}

/**
 * Props for the ReportSecurityKeyIssuesForm component.
 */
interface ReportSecurityKeyIssuesFormProps {
  /** Configuration object for customizing form content and options */
  config: FormConfig;
  /** Callback function fired when form is submitted with valid data */
  onSubmit: (formData: ReportFormData) => void;
  /** Callback function fired when user cancels the form */
  onCancel: () => void;
}

/**
 * Form for security key issue reporting and replacement delivery preferences.
 *
 * Validation:
 * - Requires reason and delivery method selection
 * - "Other" reason requires details (with character limit)
 * - All required fields must be valid for submission
 *
 * @param props - The component props
 * @param props.config - Configuration object for customizing form content
 * @param props.onSubmit - Function called when form is successfully submitted with valid data
 * @param props.onCancel - Function called when user cancels the form
 * @returns JSX element containing the complete issue reporting form
 */
export default function ReportSecurityKeyIssuesForm({
  config,
  onSubmit,
  onCancel,
}: ReportSecurityKeyIssuesFormProps): JSX.Element {
  const [selectedReason, setSelectedReason] = useState("");
  const [selectedDelivery, setSelectedDelivery] = useState("");
  const [otherReasonDetails, setOtherReasonDetails] = useState("");
  const [detailsError, setDetailsError] = useState("");
  const [formStartTime] = useState(Date.now());
  const [validationErrors, setValidationErrors] = useState({
    reason: false,
    delivery: false,
    otherDetails: false,
  });
  const intl = useIntl();

  /**
   * Handles form submission by validating data and publishing metrics.
   *
   * Collects all form data, trims whitespace from text inputs, calculates
   * completion time for metrics, publishes submission analytics, and calls
   * the onSubmit callback with the validated form data.
   */
  const handleSubmit = () => {
    const trimmedDetails = otherReasonDetails.trim();
    const newValidationErrors = {
      reason: selectedReason === "",
      delivery: selectedDelivery === "",
      otherDetails: selectedReason === "other" && trimmedDetails === "",
    };

    setValidationErrors(newValidationErrors);

    const hasAnyErrors = Object.values(newValidationErrors).some(Boolean);
    if (hasAnyErrors || detailsError !== "") {
      return;
    }

    const formData: ReportFormData = {
      selectedReason,
      selectedDelivery,
      otherReasonDetails: trimmedDetails || undefined,
    };

    const formCompletionTime = Date.now() - formStartTime;
    publishSecurityKeyIssuesFormSubmittedMetrics({
      surveyAnswers: {
        selectedReason: formData.selectedReason,
        selectedDelivery: formData.selectedDelivery,
        otherReasonDetails: trimmedDetails || undefined,
      },
      formCompletionTime,
    });

    if (selectedReason === "not_arrived") {
      publishSecurityKeyNotArrivedMetric();
    } else if (selectedReason === "not_with_me") {
      publishSecurityKeyNotWithMeMetric();
    } else if (selectedReason === "other") {
      publishSecurityKeyOtherIssueMetric({
        otherReasonDetails: trimmedDetails || undefined,
      });
    }

    onSubmit(formData);
  };

  /**
   * Handles form cancellation by collecting partial data and publishing metrics.
   *
   * Captures the current form state as partial data for analytics purposes,
   * publishes cancellation metrics to track user behavior, and calls the
   * onCancel callback to close the form interface.
   */
  const handleCancel = () => {
    const sanitizedDetails = otherReasonDetails.trim();
    const partialData = {
      selectedReason,
      selectedDelivery,
      otherReasonDetails: sanitizedDetails || undefined,
    };

    publishSecurityKeyIssuesFormCanceledMetrics({ partialData });
    onCancel();
  };

  /**
   * Handles reason selection changes and clears associated validation errors.
   *
   * Centralizes the logic for updating selected reason state and clearing
   * reason-specific validation errors to follow DRY principles and improve
   * maintainability across all reason radio button handlers.
   *
   * @param value - The selected reason value from the radio button
   */
  const handleReasonChange = (value: string) => {
    setSelectedReason(value);
    setValidationErrors((prev) => ({
      ...prev,
      reason: false,
    }));
  };

  /**
   * Handles delivery method selection changes and clears associated validation errors.
   *
   * Centralizes the logic for updating selected delivery state and clearing
   * delivery-specific validation errors to follow DRY principles and improve
   * maintainability across all delivery radio button handlers.
   *
   * @param value - The selected delivery method value from the radio button
   */
  const handleDeliveryChange = (value: string) => {
    setSelectedDelivery(value);
    setValidationErrors((prev) => ({
      ...prev,
      delivery: false,
    }));
  };

  /**
   * Handles changes to the "other reason details" text area with validation.
   *
   * Enforces character limits, updates error state for real-time feedback,
   * and manages the text area value. Provides immediate validation feedback
   * to users about character count and validation status.
   *
   * @param value - The new text area value from user input
   */
  const handleOtherReasonDetailsChange = (value: string) => {
    if (value.length > MAX_DETAILS_LENGTH) {
      setDetailsError(`Details cannot exceed ${MAX_DETAILS_LENGTH} characters`);
    } else {
      setDetailsError("");
    }
    setOtherReasonDetails(value);
  };

  const isFormValid =
    selectedReason !== "" &&
    selectedDelivery !== "" &&
    (selectedReason !== "other" || otherReasonDetails.trim() !== "") &&
    detailsError === "";

  return (
    <>
      <ModalHeader>
        <Text fontSize="T300" fontWeight="bold">
          <FormattedMessage id="insertAndTestSecurityKey-issuesForm-modalHeader" />
        </Text>
      </ModalHeader>
      <ModalBody>
        <View padding="S800">
          <Col rowGap="S400">
            <MessageBanner type={MessageBannerType.Warning}>
              <FormattedMessage id={config.warningBannerMessageId} />
            </MessageBanner>

            {Object.values(validationErrors).some(Boolean) && (
              <MessageBanner type={MessageBannerType.Error}>
                <FormattedMessage
                  id="insertAndTestSecurityKey-issuesForm-validationError-errorBanner"
                  values={{ b: (chunks: string) => <b>{chunks}</b> }}
                />
              </MessageBanner>
            )}

            <FormWrapper>
              <Text fontSize="T200" color="color.text.secondary">
                <FormattedMessage id="insertAndTestSecurityKey-issuesForm-requiredField" />
              </Text>

              <Col gridGap="S400">
                <Col gridGap="S400">
                  <Text fontWeight="medium">
                    <FormattedMessage id="insertAndTestSecurityKey-issuesForm-question1" />
                  </Text>
                  <Col gridGap="S200" padding={{ left: "S400" }}>
                    {config.question1Options.map((option) => (
                      <Row key={option.id} alignItems="center" gridGap="S200">
                        <Radio
                          id={option.id}
                          name="securityKeyReason"
                          value={option.value}
                          checked={selectedReason === option.value}
                          onChange={(e) => handleReasonChange(e.target.value)}
                          error={validationErrors.reason}
                        />
                        <Label htmlFor={option.id} color="color.text.primary">
                          <FormattedMessage id={option.labelMessageId} />
                        </Label>
                      </Row>
                    ))}
                    <Row alignItems="center" gridGap="S200">
                      <Radio
                        id="reason-other"
                        name="securityKeyReason"
                        value="other"
                        checked={selectedReason === "other"}
                        onChange={(e) => handleReasonChange(e.target.value)}
                        error={validationErrors.reason}
                      />
                      <Label htmlFor="reason-other" color="color.text.primary">
                        <FormattedMessage id="insertAndTestSecurityKey-issuesForm-question1-optionOther" />
                      </Label>
                    </Row>
                  </Col>
                  {validationErrors.reason && (
                    <View padding={{ left: "S400" }}>
                      <InputFooter error>
                        <Text fontSize="T200" color="color.text.secondary">
                          <FormattedMessage id="insertAndTestSecurityKey-issuesForm-validationError-chooseOption" />
                        </Text>
                      </InputFooter>
                    </View>
                  )}
                </Col>

                {selectedReason === "other" && (
                  <View padding={{ left: "S400" }}>
                    <InputWrapper
                      id="other-reason-details"
                      labelText={intl.formatMessage({
                        id: "insertAndTestSecurityKey-issuesForm-question1-optionOther-detailsLabel",
                      })}
                      required
                      error={validationErrors.otherDetails}
                    >
                      {(textAreaProps) => (
                        <TextArea
                          {...textAreaProps}
                          value={otherReasonDetails}
                          onChange={(e) => {
                            handleOtherReasonDetailsChange(e.target.value);
                            setValidationErrors((prev) => ({
                              ...prev,
                              otherDetails: false,
                            }));
                          }}
                          placeholder={intl.formatMessage({
                            id: "insertAndTestSecurityKey-issuesForm-question1-optionOther-detailsPlaceholder",
                          })}
                          height={TEXT_AREA_HEIGHT}
                          resize="vertical"
                          maxLength={MAX_DETAILS_LENGTH}
                        />
                      )}
                    </InputWrapper>
                    <Text fontSize="T200" color="color.text.secondary">
                      <FormattedMessage
                        id="insertAndTestSecurityKey-issuesForm-question1-optionOther-characterCount"
                        values={{
                          current: otherReasonDetails.length,
                          max: MAX_DETAILS_LENGTH,
                        }}
                      />
                    </Text>
                    {validationErrors.otherDetails && (
                      <>
                        <Spacer height="S200" />
                        <InputFooter error>
                          <Text fontSize="T200" color="color.text.secondary">
                            <FormattedMessage id="insertAndTestSecurityKey-issuesForm-validationError-provideDetails" />
                          </Text>
                        </InputFooter>
                      </>
                    )}
                  </View>
                )}

                <Col gridGap="S200">
                  <Text fontWeight="medium">
                    <FormattedMessage id="insertAndTestSecurityKey-issuesForm-question2" />
                  </Text>
                  <Col gridGap="0">
                    <View width="778px" display="flex" flexDirection="column">
                      <View display="flex">
                        <DetailedRadio
                          id="delivery-it-vending"
                          name="deliveryMethod"
                          value="it_vending_machine"
                          titleText={intl.formatMessage({
                            id: "insertAndTestSecurityKey-issuesForm-question2-option1-titleText",
                          })}
                          details={
                            <Text fontSize="T250">
                              <FormattedMessage id="insertAndTestSecurityKey-issuesForm-question2-option1-details" />
                            </Text>
                          }
                          checked={selectedDelivery === "it_vending_machine"}
                          onChange={(e) => handleDeliveryChange(e.target.value)}
                          error={validationErrors.delivery}
                          renderTitle={({ titleText }) => (
                            <Col gridGap="S200" alignItems="flex-start">
                              <Chip
                                backgroundColor={ChipBackgroundColor.Blue}
                                size={ChipSize.Small}
                              >
                                <FormattedMessage id="insertAndTestSecurityKey-issuesForm-question2-option1-chip" />
                              </Chip>
                              <Text fontWeight="bold" fontSize="T300">
                                {titleText}
                              </Text>
                            </Col>
                          )}
                        />
                      </View>

                      <Spacer height="S300" />

                      <View display="flex">
                        <DetailedRadio
                          id="delivery-chat-it"
                          name="deliveryMethod"
                          value="chat_with_it"
                          titleText={intl.formatMessage({
                            id: "insertAndTestSecurityKey-issuesForm-question2-option2-titleText",
                          })}
                          details={
                            <Text fontSize="T250">
                              <FormattedMessage id="insertAndTestSecurityKey-issuesForm-question2-option2-details" />
                            </Text>
                          }
                          checked={selectedDelivery === "chat_with_it"}
                          onChange={(e) => handleDeliveryChange(e.target.value)}
                          error={validationErrors.delivery}
                          renderTitle={({ titleText }) => (
                            <Col gridGap="S200" alignItems="flex-start">
                              <Chip
                                backgroundColor={ChipBackgroundColor.Blue}
                                size={ChipSize.Small}
                              >
                                <FormattedMessage id="insertAndTestSecurityKey-issuesForm-question2-option2-chip" />
                              </Chip>
                              <Text fontWeight="bold" fontSize="T300">
                                {titleText}
                              </Text>
                            </Col>
                          )}
                        />
                      </View>
                    </View>
                    {validationErrors.delivery && (
                      <>
                        <Spacer height="S300" />
                        <InputFooter error>
                          <Text fontSize="T200" color="color.text.secondary">
                            <FormattedMessage id="insertAndTestSecurityKey-issuesForm-validationError-chooseOption" />
                          </Text>
                        </InputFooter>
                      </>
                    )}
                  </Col>
                </Col>
              </Col>
            </FormWrapper>
          </Col>
        </View>
      </ModalBody>
      <ModalFooter>
        <Row gridGap="S300" justifyContent="flex-end" width="100%">
          <Button onClick={handleCancel} variant={ButtonVariant.Secondary}>
            <FormattedMessage id="insertAndTestSecurityKey-issuesForm-cancelAndBackToSetupButton" />
          </Button>
          <Button onClick={handleSubmit} variant={ButtonVariant.Primary}>
            <FormattedMessage id="insertAndTestSecurityKey-issuesForm-submitButton" />
          </Button>
        </Row>
      </ModalFooter>
    </>
  );
}
